import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { ModuleHeader } from "@/components/kcc/Panel";
import { useAuth } from "@/lib/auth";
import { Bot, Send, ShieldCheck, Sparkles, Loader2, ChevronRight } from "lucide-react";

const AGENT_META = {
    ceo: { color: "purple", icon: "👑" },
    coo: { color: "cobalt", icon: "🏭" },
    cfo: { color: "green", icon: "💰" },
    mining: { color: "copper", icon: "⛰️" },
    plant: { color: "cobalt", icon: "🏗️" },
    maintenance: { color: "copper", icon: "🔧" },
    reliability: { color: "amber", icon: "📈" },
    safety: { color: "green", icon: "🛡️" },
    esg: { color: "green", icon: "🌱" },
    procurement: { color: "cobalt", icon: "🛒" },
    contract: { color: "purple", icon: "📜" },
    cyber: { color: "red", icon: "🔐" },
};

export default function AIAdvisorPage() {
    const { user } = useAuth();
    const [agents, setAgents] = useState([]);
    const [current, setCurrent] = useState("ceo");
    const [messages, setMessages] = useState({});
    const [sessions, setSessions] = useState({});
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const scrollerRef = useRef(null);

    useEffect(() => {
        api.get("/ai/agents").then(({ data }) => setAgents(data.agents || []));
    }, []);

    useEffect(() => {
        scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, current, sending]);

    const agent = agents.find((a) => a.id === current);
    const msgs = messages[current] || [];

    async function send(text) {
        const message = (text ?? input).trim();
        if (!message || sending) return;
        setInput("");
        const nextMsgs = [...msgs, { role: "user", content: message }];
        setMessages((prev) => ({ ...prev, [current]: nextMsgs }));
        setSending(true);
        try {
            const { data } = await api.post("/ai/agent", {
                agent_id: current,
                message,
                session_id: sessions[current] || null,
            });
            setSessions((prev) => ({ ...prev, [current]: data.session_id }));
            setMessages((prev) => ({
                ...prev,
                [current]: [...nextMsgs, { role: "assistant", content: data.reply, sources: data.sources || [], agent_name: data.agent_name }],
            }));
        } catch (e) {
            setMessages((prev) => ({
                ...prev,
                [current]: [...nextMsgs, { role: "assistant", content: `⚠︎ ${e.response?.data?.detail || e.message}`, sources: [] }],
            }));
        } finally {
            setSending(false);
        }
    }

    const suggestions = {
        ceo: ["Give me a 3-line strategic briefing.", "What's the single most important decision I need to make this week?"],
        coo: ["What's my top operational bottleneck?", "Where is the largest tonne loss coming from?"],
        cfo: ["What is driving the variance in cash cost of copper?", "Which cost centres are over budget?"],
        mining: ["Which pit is causing the biggest delay?", "Rank my top 5 delay codes."],
        plant: ["Explain the Cu recovery trend.", "Where can I lift recovery by 1%?"],
        maintenance: ["Which P1 work orders need me first?", "Any assets awaiting parts >7 days?"],
        reliability: ["What are my top predicted failures this month?", "Which assets are bad actors?"],
        safety: ["Any HiPo trends I should worry about?", "Are corrective actions closing on time?"],
        esg: ["Any environmental exceedances I should escalate?", "What's the TSF governance status?"],
        procurement: ["Where is my open commitment highest?", "Any maverick-spend indicators?"],
        contract: ["Any contracts renewing in the next 90 days?", "Highlight the highest risk contractor."],
        cyber: ["What's my current SOC threat level?", "Any critical vulnerabilities open?"],
    };

    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="AI Intelligence Engine · 12 specialised agents"
                title="Executive AI Command"
                description="Specialised AI agents for every leadership function — each grounded in live KCC360 data, each with confidence scores, source citations and required human approval for critical recommendations."
                actions={<div className="text-[11px] kcc-text-silver">Model: Claude Sonnet 4.5 · Emergent Universal Key · governed via KCC360 AI orchestration</div>}
            />

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                <div className="kcc-panel p-3 xl:sticky xl:top-24 max-h-[75vh] overflow-y-auto">
                    <div className="kcc-label px-2 pb-2">Select an agent</div>
                    <div className="space-y-1">
                        {agents.map((a) => {
                            const meta = AGENT_META[a.id] || { color: "purple", icon: "🤖" };
                            const active = a.id === current;
                            return (
                                <button
                                    key={a.id}
                                    onClick={() => setCurrent(a.id)}
                                    className={`w-full text-left px-3 py-2.5 rounded-md flex items-center gap-3 border transition-colors ${active ? "border-white/25 bg-white/[0.04] text-white" : "border-transparent text-kcc-silver hover:text-white hover:bg-white/[0.03]"}`}
                                    data-testid={`agent-${a.id}`}
                                >
                                    <span className="text-lg leading-none">{meta.icon}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold truncate">{a.name}</div>
                                        <div className="text-[10.5px] kcc-text-dim truncate">{a.focus.slice(0, 60)}…</div>
                                    </div>
                                    {active && <ChevronRight className="w-4 h-4 text-kcc-copper-light" />}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="xl:col-span-3 kcc-panel flex flex-col" style={{ minHeight: "70vh" }}>
                    <header className="p-4 border-b border-white/5 flex items-center gap-3">
                        <div className="w-11 h-11 rounded-md flex items-center justify-center bg-kcc-purple/15 border border-kcc-purple/30 text-2xl">
                            {AGENT_META[current]?.icon || "🤖"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display text-lg font-semibold text-white">{agent?.name || "KCC360 AI"}</div>
                            <div className="text-[11px] kcc-text-silver flex items-center gap-1.5">
                                <ShieldCheck className="w-3 h-3 text-kcc-green" /> Governed · grounded in KCC360 · human-in-the-loop
                            </div>
                        </div>
                    </header>

                    <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                        {msgs.length === 0 && (
                            <div className="text-sm kcc-text-silver bg-white/[0.03] border border-white/10 rounded-lg p-4">
                                {agent?.focus || "Ask this agent anything within its remit. It answers using live KCC360 data."}
                            </div>
                        )}
                        {msgs.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[86%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                                        m.role === "user"
                                            ? "bg-kcc-cobalt/15 border border-kcc-cobalt/25 text-white"
                                            : "bg-white/[0.03] border border-white/8 text-kcc-silver"
                                    }`}
                                    data-testid={`msg-${m.role}-${i}`}
                                >
                                    <div className="whitespace-pre-wrap">{m.content}</div>
                                    {m.sources?.length > 0 && (
                                        <div className="mt-2 pt-2 border-t border-white/5 text-[10.5px] kcc-text-dim flex flex-wrap gap-1.5">
                                            <Sparkles className="w-3 h-3 text-kcc-purple" />
                                            {m.sources.map((s) => (
                                                <span key={s} className="px-1.5 py-0.5 rounded border border-white/10">{s}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start">
                                <div className="bg-white/[0.03] border border-white/8 rounded-lg px-3.5 py-2.5 text-sm kcc-text-silver flex items-center gap-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
                                </div>
                            </div>
                        )}
                    </div>

                    {msgs.length === 0 && (
                        <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                            {(suggestions[current] || []).map((s) => (
                                <button
                                    key={s}
                                    onClick={() => send(s)}
                                    className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/10 hover:border-kcc-purple/50 hover:bg-kcc-purple/10 text-kcc-silver hover:text-white"
                                    data-testid="agent-suggestion"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/5 flex items-end gap-2">
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            rows={1}
                            placeholder={`Ask the ${agent?.name || "agent"}…`}
                            className="flex-1 bg-white/[0.03] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-kcc-purple/50 resize-none max-h-32"
                            data-testid="agent-input"
                        />
                        <button
                            type="submit"
                            disabled={sending || !input.trim()}
                            className="bg-kcc-copper hover:bg-kcc-copper-light disabled:opacity-40 text-white rounded-md px-3 py-2"
                            data-testid="agent-send"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
